import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ShieldCheck, ShieldAlert, ShieldX, CheckCircle2, Circle, MinusCircle, AlertTriangle, Clock,
  Upload, Download, ExternalLink, History, DollarSign, RefreshCw, Loader2, Ban, ChevronDown, ChevronRight,
  FileText, Star, Scale, SlidersHorizontal,
} from "lucide-react";
import toast from "react-hot-toast";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "../../../components/ui";
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";
import { formatMelbourneDateTime, formatMelbourneShortDate } from "../../../utils/melbourneTime";
import {
  clearRequirementNotApplicable, exportComplianceFile, getComplianceFile, markRequirementNotApplicable,
  recomputeCompliance, reviewComplianceDocument, setComplianceDocumentCurrent, setDeliveryPartyRole,
  voidComplianceDocument,
} from "../api/complianceApi";
import UploadComplianceDocDialog from "./UploadComplianceDocDialog";
import ReasonDialog from "./ReasonDialog";
import VendorNotifiedDialog from "./VendorNotifiedDialog";
import PricingEvidenceCard from "./PricingEvidenceCard";
import ComplianceSettingsDialog from "./ComplianceSettingsDialog";

const STATUS_META = {
  compliant: { label: "Compliant", icon: ShieldCheck, classes: "bg-green-50 text-green-800 border-green-200", dot: "bg-green-500" },
  action_required: { label: "Action Required", icon: ShieldAlert, classes: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  cannot_go_live: { label: "Cannot Go Live", icon: ShieldX, classes: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500" },
};

const ITEM_STATUS = {
  current: { label: "Current", icon: CheckCircle2, classes: "text-green-700" },
  received: { label: "Received", icon: Clock, classes: "text-amber-700" },
  superseded: { label: "Superseded", icon: AlertTriangle, classes: "text-amber-700" },
  expired: { label: "Expired", icon: AlertTriangle, classes: "text-red-700" },
  missing: { label: "Not received", icon: Circle, classes: "text-red-600" },
  na: { label: "N/A", icon: MinusCircle, classes: "text-gray-400" },
};

const DOC_STATUS_BADGE = {
  current: "bg-green-100 text-green-800",
  reviewed: "bg-blue-100 text-blue-800",
  received: "bg-amber-100 text-amber-800",
  superseded: "bg-gray-100 text-gray-600",
  voided: "bg-red-100 text-red-700 line-through",
};

const SOURCE_LABEL = {
  esign: "Collings eSign", upload: "Uploaded", drive: "Google Drive", generated: "Generated",
  system: "System", backfill: "Existing record",
};

const money = (v) => (v === null || v === undefined || v === "" ? "" : `$${Number(v).toLocaleString()}`);
const range = (low, high) => {
  if (low == null && high == null) return "—";
  if (low != null && high != null && Number(low) !== Number(high)) return `${money(low)} – ${money(high)}`;
  return money(low ?? high);
};

function StatusHeader({ file, onRecompute, busy, onExport, exporting, onSettings }) {
  const meta = STATUS_META[file.listing_status] || STATUS_META.cannot_go_live;
  const Icon = meta.icon;
  return (
    <div className={`rounded-2xl border p-5 ${meta.classes}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Icon className="h-10 w-10 shrink-0" />
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.2em] opacity-70">Listing compliance</div>
            <div className="text-2xl font-black">{meta.label}</div>
            {file.sale_status !== "not_applicable" && (
              <div className="mt-1 text-sm font-semibold">
                Sale compliance: {file.sale_status === "complete" ? "Complete" : "Incomplete"}
              </div>
            )}
            <div className="mt-1 text-xs opacity-70">
              {file.is_residential ? "Residential sale" : "Non-residential sale (PPS rules do not apply)"}
              {file.computed_at ? ` · checked ${formatMelbourneDateTime(file.computed_at)}` : ""}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onExport("pdf")} disabled={!!exporting}>
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-2">Export PDF</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => onExport("zip")} disabled={!!exporting}>
            {exporting === "zip" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-2">Export all files</span>
          </Button>
          {onSettings && (
            <Button variant="outline" size="sm" onClick={onSettings} title="Compliance gate settings">
              <SlidersHorizontal className="h-4 w-4" />
              <span className="ml-2">Gates</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onRecompute} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Re-check</span>
          </Button>
        </div>
      </div>
      {file.flags?.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm">
          {file.flags.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${f.severity === "block" ? "bg-red-500" : "bg-amber-500"}`} />
              <span>{f.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VersionRow({ doc, isAdmin, onAction, busyId }) {
  const busy = busyId === doc.id;
  const adminOnly = ["sales_authority", "executed_contract"].includes(doc.requirement_key);
  const canMakeCurrent = doc.status !== "current" && doc.status !== "voided" && (isAdmin || !adminOnly);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="h-4 w-4 text-gray-400 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium truncate" title={doc.display_name}>
            {doc.display_name || `Version ${doc.version}`}
            {doc.status === "current" && doc.requirement_key === "pps" && (
              <Star className="inline h-3.5 w-3.5 ml-1 text-yellow-500" title="Main PPS on REA, Domain & portal" />
            )}
          </div>
          <div className="text-xs text-gray-500">
            v{doc.version} · {SOURCE_LABEL[doc.source] || doc.source}
            {doc.executed_at ? ` · ${formatMelbourneShortDate(doc.executed_at)}` : ""}
            {doc.created_by_name ? ` · ${doc.created_by_name}` : ""}
            {doc.void_reason ? ` · voided: ${doc.void_reason}` : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase ${DOC_STATUS_BADGE[doc.status] || ""}`}>
          {doc.status}
        </span>
        {doc.file_url && (
          <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="Open">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
        {doc.status === "received" && (
          <Button size="sm" variant="outline" disabled={busy || (adminOnly && !isAdmin)}
            onClick={() => onAction("review", doc)} title={adminOnly && !isAdmin ? "Admin review required" : "Mark reviewed & current"}>
            Mark reviewed
          </Button>
        )}
        {doc.status !== "received" && canMakeCurrent && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAction("set-current", doc)}>Set current</Button>
        )}
        {isAdmin && doc.status !== "voided" && (
          <Button size="sm" variant="ghost" className="text-red-600" disabled={busy} onClick={() => onAction("void", doc)} title="Void (kept on record)">
            <Ban className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ChecklistItem({ item, isAdmin, onUpload, onNa, onClearNa, onDocAction, busyId }) {
  const [open, setOpen] = useState(false);
  const meta = ITEM_STATUS[item.status] || ITEM_STATUS.missing;
  const Icon = meta.icon;
  const docs = item.documents || [];
  const uploadable = item.status !== "na";
  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-3 py-3">
        <button type="button" className="flex items-center gap-3 text-left min-w-0" onClick={() => setOpen((o) => !o)}>
          <Icon className={`h-5 w-5 shrink-0 ${meta.classes}`} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 flex items-center gap-2">
              {item.label}
              {!item.is_required && item.status !== "na" && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">optional</span>
              )}
              {item.blocking && item.is_required && item.status !== "current" && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-500">blocks go-live</span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {item.status === "na" ? `Not applicable${item.na_reason ? `: ${item.na_reason}` : ""}` : meta.label}
              {docs.length ? ` · ${docs.length} version${docs.length > 1 ? "s" : ""}` : ""}
            </div>
          </div>
          {docs.length > 0 && (open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />)}
        </button>
        <div className="flex items-center gap-2">
          {uploadable && (
            <Button size="sm" variant="outline" onClick={() => onUpload(item)}>
              <Upload className="h-4 w-4 mr-1" /> Upload
            </Button>
          )}
          {isAdmin && item.status !== "na" && (
            <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => onNa(item)} title="Mark not applicable">N/A</Button>
          )}
          {isAdmin && item.status === "na" && (
            <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => onClearNa(item)}>Applicable</Button>
          )}
        </div>
      </div>
      {open && docs.length > 0 && (
        <div className="pb-3 pl-8 space-y-2">
          {docs.map((d) => <VersionRow key={d.id} doc={d} isAdmin={isAdmin} onAction={onDocAction} busyId={busyId} />)}
        </div>
      )}
      {item.description && open && <p className="pb-3 pl-8 text-xs text-gray-500">{item.description}</p>}
    </div>
  );
}

function Checklist({ title, items, ...rest }) {
  if (!items.length) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-black uppercase tracking-[0.15em] text-gray-500">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items.map((item) => <ChecklistItem key={item.requirement_key} item={item} {...rest} />)}
      </CardContent>
    </Card>
  );
}

const GATE_NAMES = { on_market_gate: "going live", portal_publish_gate: "advertising online", cos_send_gate: "sending a contract" };
const blockedGates = (settings) =>
  Object.entries(GATE_NAMES).filter(([k]) => settings?.[k] === "block").map(([, v]) => v);

const STAMP = (v) => (v ? formatMelbourneShortDate(v) : "—");

function Deliveries({ deliveries, onSetRole }) {
  if (!deliveries?.length) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-black uppercase tracking-[0.15em] text-gray-500">
          Executed contract receipts
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr><th className="py-2 pr-3">Party</th><th className="py-2 pr-3">Recipient</th><th className="py-2 pr-3">Sent</th><th className="py-2 pr-3">Delivered</th><th className="py-2 pr-3">Opened</th><th className="py-2">Acknowledged</th></tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id} className="border-t border-gray-100">
                  <td className="py-2 pr-3">
                    {d.party_role === "unknown" ? (
                      <select className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs" defaultValue=""
                        aria-label={`Party for ${d.email}`} onChange={(e) => e.target.value && onSetRole(d, e.target.value)}>
                        <option value="" disabled>Buyer or vendor?</option>
                        <option value="buyer">Buyer</option>
                        <option value="vendor">Vendor</option>
                      </select>
                    ) : <span className="capitalize">{d.party_role}</span>}
                  </td>
                  <td className="py-2 pr-3">{d.name || d.email}<div className="text-xs text-gray-500">{d.email}</div></td>
                  <td className="py-2 pr-3 text-gray-600">{STAMP(d.sent_at)}</td>
                  <td className="py-2 pr-3 text-gray-600">{STAMP(d.delivered_at)}</td>
                  <td className="py-2 pr-3 text-gray-600">{STAMP(d.opened_at)}</td>
                  <td className={`py-2 font-medium ${d.acknowledged_at ? "text-green-700" : "text-amber-700"}`}>
                    {d.acknowledged_at ? STAMP(d.acknowledged_at) : "Awaiting"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

const saveBlob = (data, filename) => {
  const url = window.URL.createObjectURL(data instanceof Blob ? data : new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const filenameFrom = (headers, fallback) => {
  const cd = headers?.["content-disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  return m ? m[1] : fallback;
};

// Price changes the vendor must be told about in writing.
const NEEDS_VENDOR_NOTICE = ["advertised_price", "estimated_selling_price", "indicative_price", "auction_reserve"];

function PriceHistory({ events, onVendorNotified }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-black uppercase tracking-[0.15em] text-gray-500 flex items-center gap-2">
          <DollarSign className="h-4 w-4" /> Price compliance history
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!events.length ? (
          <p className="text-sm text-gray-500 py-2">No price events recorded yet. The signed authority's estimate, PPS indicative price, advertised price changes, reserve and rejected written offers appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                <tr><th className="py-2 pr-3">When</th><th className="py-2 pr-3">Price</th><th className="py-2 pr-3">Was</th><th className="py-2 pr-3">Now</th><th className="py-2 pr-3">Source</th><th className="py-2 pr-3">Reason</th><th className="py-2">Vendor notified</th></tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{formatMelbourneDateTime(e.occurred_at)}</td>
                    <td className="py-2 pr-3 font-medium">{e.price_kind_label}</td>
                    <td className="py-2 pr-3 text-gray-500">{range(e.old_low, e.old_high) === "—" ? (e.display_text_old || "—") : range(e.old_low, e.old_high)}</td>
                    <td className="py-2 pr-3 font-semibold">{range(e.new_low, e.new_high) === "—" ? (e.display_text_new || "—") : range(e.new_low, e.new_high)}</td>
                    <td className="py-2 pr-3 text-gray-600">{e.source?.replace(/_/g, " ")}{e.actor_name ? ` · ${e.actor_name}` : ""}</td>
                    <td className="py-2 pr-3 text-gray-600">{e.reason || "—"}</td>
                    <td className="py-2 text-gray-600">
                      {e.vendor_notified_at
                        ? `${formatMelbourneShortDate(e.vendor_notified_at)} ${e.vendor_notified_how || ""}`
                        : NEEDS_VENDOR_NOTICE.includes(e.price_kind) && e.old_low != null
                          ? <Button size="sm" variant="outline" onClick={() => onVendorNotified(e)}>Record</Button>
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AuditLog({ events }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? events : events.slice(0, 25);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-black uppercase tracking-[0.15em] text-gray-500 flex items-center gap-2">
          <History className="h-4 w-4" /> Compliance audit log
          <span className="ml-auto text-[10px] font-semibold normal-case tracking-normal text-gray-400">append-only · cannot be edited or deleted</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!rows.length ? <p className="text-sm text-gray-500 py-2">No events yet.</p> : (
          <ol className="divide-y divide-gray-100">
            {rows.map((e) => (
              <li key={e.id} className="py-2 flex gap-3 text-sm">
                <span className="w-36 shrink-0 text-gray-500 whitespace-nowrap">{formatMelbourneDateTime(e.occurred_at)}</span>
                <span className="flex-1">
                  <span className="font-medium">{e.summary}</span>
                  <span className="text-gray-400"> · {e.actor_name || e.actor_email || "System"}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
        {events.length > 25 && (
          <Button variant="ghost" size="sm" onClick={() => setShowAll((s) => !s)}>{showAll ? "Show less" : `Show all ${events.length}`}</Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The property Compliance tab (DOMA 12954696393): one central compliance file
 * per sales property - checklist of mandatory VIC sale documents with every
 * version kept, price compliance history, and the append-only audit log.
 */
export default function ComplianceTab({ propertyId }) {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const isSuperAdmin = hasSuperAdminAccess(user);
  const [showSettings, setShowSettings] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyDocId, setBusyDocId] = useState(null);
  const [uploadItem, setUploadItem] = useState(null);
  const [reason, setReason] = useState(null); // { kind: 'void'|'na', doc?, item? }
  const [noticeFor, setNoticeFor] = useState(null); // price event awaiting a vendor notice
  const [exporting, setExporting] = useState(null); // "pdf" | "zip" while downloading

  const load = useCallback(async (opts) => {
    if (!propertyId) return;
    try {
      const res = await getComplianceFile(propertyId, opts);
      setFile(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load the compliance file.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try { const res = await recomputeCompliance(propertyId); setFile(res.data); }
    catch { toast.error("Re-check failed"); }
    finally { setBusy(false); }
  };

  const onDocAction = async (action, doc) => {
    if (action === "void") { setReason({ kind: "void", doc }); return; }
    setBusyDocId(doc.id);
    try {
      if (action === "review") await reviewComplianceDocument(doc.id);
      else if (action === "set-current") await setComplianceDocumentCurrent(doc.id);
      toast.success(action === "review" ? "Marked reviewed and current" : "Set as current");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusyDocId(null); }
  };

  const confirmReason = async (text) => {
    setBusy(true);
    try {
      if (reason.kind === "void") { await voidComplianceDocument(reason.doc.id, text); toast.success("Version voided (kept on record)"); }
      else if (reason.kind === "na") { await markRequirementNotApplicable(propertyId, reason.item.requirement_key, text); toast.success("Marked not applicable"); }
      setReason(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const onExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await exportComplianceFile(propertyId, kind);
      saveBlob(res.data, filenameFrom(res.headers, `compliance-${propertyId}.${kind}`));
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  const onSetRole = async (delivery, role) => {
    try {
      const res = await setDeliveryPartyRole(propertyId, delivery.id, role);
      setFile(res.data);
      toast.success(`Recorded as the ${role}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const clearNa = async (item) => {
    try { await clearRequirementNotApplicable(propertyId, item.requirement_key); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };

  const groups = useMemo(() => {
    const items = file?.items || [];
    return {
      listing: items.filter((i) => i.phase === "listing"),
      sale: items.filter((i) => i.phase === "sale"),
      conditional: items.filter((i) => i.phase === "conditional"),
    };
  }, [file]);

  if (loading) return <div className="flex items-center gap-2 text-gray-500 p-8"><Loader2 className="h-5 w-5 animate-spin" /> Loading compliance file…</div>;
  if (error) return (
    <div className="p-8 text-sm text-gray-600 flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-2"><Scale className="h-5 w-5 text-gray-400" /> {error}</span>
      <Button variant="outline" size="sm" onClick={() => { setLoading(true); setError(null); load(); }}>
        <RefreshCw className="h-4 w-4" /><span className="ml-2">Retry</span>
      </Button>
    </div>
  );
  if (!file) return null;

  const listProps = { isAdmin, onUpload: setUploadItem, onNa: (item) => setReason({ kind: "na", item }), onClearNa: clearNa, onDocAction, busyId: busyDocId };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <StatusHeader file={file} onRecompute={recompute} busy={busy} onExport={onExport} exporting={exporting}
        onSettings={isSuperAdmin ? () => setShowSettings(true) : null} />
      {blockedGates(file.settings).length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-800">
          Blocking is on for {blockedGates(file.settings).join(", ")}: missing documents stop those actions.
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Checklist title="Listing compliance" items={groups.listing} {...listProps} />
        <div className="space-y-6">
          <Checklist title="After sale" items={groups.sale} {...listProps} />
          <Checklist title="Conditional" items={groups.conditional} {...listProps} />
        </div>
      </div>
      <Deliveries deliveries={file.deliveries || []} onSetRole={onSetRole} />
      <PricingEvidenceCard propertyId={propertyId} onSaved={() => load()} />
      <PriceHistory events={file.price_events || []} onVendorNotified={setNoticeFor} />
      <AuditLog events={file.events || []} />

      <UploadComplianceDocDialog
        open={!!uploadItem} propertyId={propertyId} item={uploadItem}
        onClose={() => setUploadItem(null)} onUploaded={() => load()}
      />
      <VendorNotifiedDialog
        open={!!noticeFor} propertyId={propertyId} priceEvent={noticeFor}
        onClose={() => setNoticeFor(null)} onSaved={(data) => data && setFile(data)}
      />
      <ComplianceSettingsDialog open={showSettings} onClose={() => { setShowSettings(false); load(); }} />
      <ReasonDialog
        open={!!reason}
        title={reason?.kind === "void" ? `Void ${reason.doc?.display_name || "this version"}?` : `Mark "${reason?.item?.label}" not applicable`}
        description={reason?.kind === "void" ? "The version stays on record as voided; the previous current version is restored." : "Only for requirements that genuinely do not apply to this sale."}
        confirmLabel={reason?.kind === "void" ? "Void version" : "Mark N/A"}
        variant={reason?.kind === "void" ? "danger" : "default"}
        loading={busy} onConfirm={confirmReason} onClose={() => setReason(null)}
      />
    </div>
  );
}
